// API client for the Real Estate Tech backend.
//
// Source-of-truth gate (shouldUseApi):
//   Demo mode (?demo=true in URL / session flag) → ALWAYS use seed data.
//   Else if a JWT is in localStorage → call the API.
//   Else if VITE_USE_API=true → call the API.
//   Else → seed data.
//
// When a helper calls the API and fails, it re-raises the error so callers
// (useApiData) can render an error banner with retry. Pages should still
// have a seed fallback they can render silently when the API is unreachable.

import axios from 'axios';

import {
  leads as seedLeads,
  leadById as seedLeadById,
  agents as seedAgents,
  verifications as seedVerifications,
  verificationByLeadId as seedVerificationByLeadId,
  conversations as seedConversations,
  conversationsByLeadId as seedConversationsByLeadId,
  showings as seedShowings,
  showingsByLeadId as seedShowingsByLeadId,
  activities as seedActivities,
  activitiesByLeadId as seedActivitiesByLeadId,
  properties as seedProperties,
  projects as seedProjects,
} from '../data/seedData.js';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/re';
export const USE_API =
  String(import.meta.env.VITE_USE_API || '').toLowerCase() === 'true';

export const api = axios.create({
  baseURL,
  timeout: 15000,
});

// In-memory token — AuthContext pushes it here via setAuthToken so the
// axios interceptor always sees the current session without touching
// localStorage on every request.
let _authToken = null;
if (typeof window !== 'undefined') {
  try {
    _authToken = window.localStorage.getItem('atlacis_jwt');
  } catch {
    /* ignore */
  }
}

export function setAuthToken(token) {
  _authToken = token || null;
}

export function getAuthToken() {
  return _authToken;
}

export function isDemoMode() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem('atlacis_demo_mode') === 'true') return true;
    const params = new URLSearchParams(window.location.search);
    return (params.get('demo') || '').toLowerCase() === 'true';
  } catch {
    return false;
  }
}

/** The single gate every helper uses. */
export function shouldUseApi() {
  if (isDemoMode()) return false;
  if (_authToken) return true;
  if (USE_API) return true;
  return false;
}

// ── 401 / 402 subscribers ──────────────────────────────────────────────────

const _unauthorizedListeners = new Set();
export function subscribeUnauthorized(listener) {
  _unauthorizedListeners.add(listener);
  return () => _unauthorizedListeners.delete(listener);
}

const _paymentRequiredListeners = new Set();
export function subscribePaymentRequired(listener) {
  _paymentRequiredListeners.add(listener);
  return () => _paymentRequiredListeners.delete(listener);
}

// Requests to these paths shouldn't fire the auto-logout hook — a failed
// login is expected to return 401 without clobbering any existing session.
const UNAUTH_EXEMPT = ['/auth/login'];

api.interceptors.request.use((config) => {
  if (_authToken) {
    config.headers.Authorization = `Bearer ${_authToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (status === 401 && !UNAUTH_EXEMPT.some((p) => url.includes(p))) {
      _unauthorizedListeners.forEach((fn) => {
        try {
          fn(error);
        } catch {
          /* ignore */
        }
      });
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem('atlacis_jwt');
        } catch {
          /* ignore */
        }
        setAuthToken(null);
        // Give the app a beat to flush state, then redirect.
        const onLogin = window.location.pathname.startsWith('/login');
        if (!onLogin && !isDemoMode()) {
          window.setTimeout(() => {
            window.location.href = '/login';
          }, 0);
        }
      }
    }
    if (status === 402) {
      _paymentRequiredListeners.forEach((fn) => {
        try {
          fn(error);
        } catch {
          /* ignore */
        }
      });
    }
    return Promise.reject(error);
  }
);

// ── normalizers ──────────────────────────────────────────────────────────────
// The backend returns snake_case fields; our pages were built against the
// camelCase seed shape. Normalize at the edge so pages don't need changes.

function _num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function normalizeLead(api) {
  if (!api) return null;
  // If it already looks like a seed shape (has assignedAgentId), pass through.
  if ('assignedAgentId' in api && 'budget' in api) return api;
  const propTypes = api.property_type_prefs || [];

  // Denormalized agent name/email arrive on the list response; the detail
  // response also includes the full assigned_agent object. Either builds
  // the same compact shape the Leads table consumes.
  const agentFromDetail = api.assigned_agent || null;
  const agent =
    agentFromDetail ||
    (api.assigned_agent_name || api.assigned_agent_email
      ? {
          id: api.assigned_agent_id || null,
          name: api.assigned_agent_name || null,
          email: api.assigned_agent_email || null,
        }
      : null);

  // Latest verification — on list responses the backend sends just
  // score + confidence; the detail response sends the full 7-dimension
  // object. Normalize both to { status, score }.
  let verification = null;
  if (api.verification && typeof api.verification === 'object') {
    verification = {
      status: api.verification.confidence_label || null,
      score: api.verification.overall_score ?? null,
      ...api.verification,
    };
  } else if (
    api.verification_confidence !== undefined ||
    api.verification_score !== undefined
  ) {
    verification = {
      status: api.verification_confidence ?? null,
      score: api.verification_score ?? null,
    };
  }

  return {
    id: api.id,
    name: api.name,
    email: api.email,
    phone: api.phone,
    preferredLanguage: api.language,
    language: api.language,
    source: api.source,
    temperature: api.temperature,
    status: api.status,
    score: api.score ?? 0,
    confidenceLabel: api.confidence_label,
    assignedAgentId: api.assigned_agent_id || null,
    agent,
    verification,
    budget: {
      min: _num(api.budget_min),
      max: _num(api.budget_max),
      currency: 'USD',
    },
    timeline: api.timeline,
    neighborhoods: api.location_prefs || [],
    propertyType: Array.isArray(propTypes) ? propTypes[0] || null : null,
    financingType: api.financing_type,
    preApprovalStatus: api.pre_approval_status,
    preApprovalLender: api.pre_approval_lender,
    occupation: api.occupation || null,
    notes: api.notes || '',
    tags: api.tags || [],
    firstContactAt: api.first_contact_at || api.created_at,
    lastContactAt:
      api.last_activity_at || api.last_contact_at || api.updated_at || api.created_at,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    enrichmentData: api.enrichment_data,
    demoBrief: api.demo_brief || null,
    interestedPropertyIds: api.interested_property_ids || [],
  };
}

export function normalizeAgent(api) {
  if (!api) return null;
  if ('initials' in api && 'avatarColor' in api) return api;
  const initials = (api.name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return {
    id: api.id,
    name: api.name,
    initials,
    email: api.email,
    phone: api.phone,
    role: api.role,
    title: api.title || '',
    languages: api.languages || [],
    specialties: api.specializations || api.specialties || [],
    avatarColor: api.avatar_color || '#EA580C',
    stats: api.stats || {},
  };
}

// Transform the backend verification row (snake_case with per-dimension
// *_score and *_data JSONB columns) into the seed-shape the Lead Detail
// page's VerificationScorecard consumes: { overallScore, status,
// dimensions: { key: { score, evidence } }, flags, notes }.
const VERIFICATION_DIMENSIONS = [
  'employment',
  'company',
  'property_history',
  'social_presence',
  'budget_alignment',
  'location',
  'lender',
];

export function normalizeVerification(api) {
  if (!api) return null;
  if ('dimensions' in api && 'overallScore' in api) return api;

  const dimensions = {};
  for (const key of VERIFICATION_DIMENSIONS) {
    const score = api[`${key}_score`];
    if (score === null || score === undefined) continue;
    const data = api[`${key}_data`] || {};
    dimensions[key] = {
      score,
      evidence: data.evidence || null,
    };
  }

  const flags = Array.isArray(api.red_flags)
    ? api.red_flags.map((f) =>
        typeof f === 'string'
          ? { dimension: 'overall', message: f, severity: 'error' }
          : {
              dimension: f.dimension || 'overall',
              message: f.message || String(f),
              severity: f.severity || 'error',
            },
      )
    : [];

  // Map the backend's confidence_label vocabulary to the 'status' the
  // Lead Detail summary helper expects ('passed' | 'flagged' | 'pending').
  const label = api.confidence_label || null;
  let status = 'pending';
  if (label === 'verified' || label === 'likely') status = 'passed';
  else if (label === 'red_flag') status = 'flagged';
  else if (label === 'uncertain') status = 'pending';

  return {
    id: api.id,
    leadId: api.lead_id,
    overallScore: api.overall_score ?? 0,
    confidenceLabel: label,
    status,
    dimensions,
    flags,
    notes: api.notes || null,
    verifiedAt: api.verified_at,
    createdAt: api.created_at,
  };
}

export function normalizeActivity(api) {
  if (!api) return null;
  if ('createdAt' in api && 'leadId' in api) return api;
  return {
    id: api.id,
    leadId: api.lead_id,
    agentId: api.agent_id,
    agentName: api.agent_name || null,
    type: api.type,
    description: api.description,
    metadata: api.metadata || null,
    createdAt: api.created_at,
  };
}

export function normalizeConversation(api) {
  if (!api) return null;
  if ('startedAt' in api && 'messages' in api) return api;
  const transcript = api.transcript || [];
  const lead = api.lead_name || api.lead_phone
    ? {
        id: api.lead_id || null,
        name: api.lead_name || null,
        phone: api.lead_phone || null,
      }
    : null;
  return {
    id: api.id,
    leadId: api.lead_id,
    lead,
    leadName: api.lead_name || null,
    leadPhone: api.lead_phone || null,
    channel: api.channel,
    language: api.language,
    direction: api.direction,
    startedAt: api.started_at,
    endedAt: api.ended_at,
    outcome: api.status,
    summary: api.summary || '',
    messages: transcript.map((m) => ({
      at: m.at || m.timestamp,
      from: m.role === 'lead' || m.role === 'user' ? 'lead' : m.role === 'ai' ? 'ai' : 'agent',
      text: m.text || m.content || '',
      agentId: m.agent_id,
    })),
    escalation: api.escalation_reason
      ? { reason: api.escalation_reason, trigger: api.escalation_reason }
      : undefined,
  };
}

export function normalizeShowing(api) {
  if (!api) return null;
  if ('scheduledAt' in api) return api;

  // Denormalized parent fields the backend now joins in so the Showings
  // table can render without a separate fetch. Present only on list
  // responses — fall back to null when older-shape data comes through.
  const lead = api.lead_name || api.lead_phone
    ? {
        id: api.lead_id || null,
        name: api.lead_name || null,
        phone: api.lead_phone || null,
      }
    : null;

  const property = api.property
    ? api.property
    : api.property_address
      ? { id: api.property_id || null, address: api.property_address }
      : null;

  const agent = api.agent_name
    ? { id: api.agent_id || null, name: api.agent_name }
    : null;

  return {
    id: api.id,
    leadId: api.lead_id,
    propertyId: api.property_id,
    agentId: api.agent_id,
    scheduledAt: api.scheduled_at,
    durationMinutes: api.duration_minutes,
    status: api.status,
    confirmedAt: api.confirmed_at,
    completedAt: api.completed_at,
    notes: api.notes || api.outcome_notes || '',
    lead,
    property,
    agent,
    leadName: api.lead_name || null,
    leadPhone: api.lead_phone || null,
    propertyAddress: api.property_address || null,
    agentName: api.agent_name || null,
  };
}

// ── Typed helpers ───────────────────────────────────────────────────────────

export async function listLeads(params = {}) {
  if (shouldUseApi()) {
    const { data } = await api.get('/leads', { params });
    return {
      ...data,
      leads: (data.leads || []).map(normalizeLead),
    };
  }
  let rows = seedLeads.slice();
  if (params.source) rows = rows.filter((l) => l.source === params.source);
  if (params.status) rows = rows.filter((l) => l.status === params.status);
  if (params.agent_id) rows = rows.filter((l) => l.assignedAgentId === params.agent_id);
  if (params.score_min != null) rows = rows.filter((l) => (l.score ?? 0) >= params.score_min);
  if (params.score_max != null) rows = rows.filter((l) => (l.score ?? 0) <= params.score_max);
  if (params.search) {
    const q = String(params.search).toLowerCase();
    rows = rows.filter(
      (l) =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.email || '').toLowerCase().includes(q) ||
        (l.phone || '').toLowerCase().includes(q)
    );
  }
  const total = rows.length;
  const offset = params.offset || 0;
  const limit = params.limit || 50;
  return {
    leads: rows.slice(offset, offset + limit),
    total,
    limit,
    offset,
    role: 'team_leader',
  };
}

export async function getLead(id) {
  if (shouldUseApi()) {
    const { data } = await api.get(`/leads/${id}`);
    return {
      ...normalizeLead(data),
      verification: normalizeVerification(data.verification),
      verifications: (data.verifications || []).map(normalizeVerification),
      activities: (data.activities || []).map(normalizeActivity),
      conversations: (data.conversations || []).map(normalizeConversation),
      showings: (data.showings || []).map(normalizeShowing),
    };
  }
  const lead = seedLeadById[id];
  if (!lead) return null;
  return {
    ...lead,
    verification: seedVerificationByLeadId[id] || null,
    verifications: seedVerifications.filter((v) => v.leadId === id),
    activities: seedActivitiesByLeadId[id] || [],
    conversations: seedConversationsByLeadId[id] || [],
    showings: seedShowingsByLeadId[id] || [],
  };
}

export async function getAttention() {
  if (shouldUseApi()) {
    const { data } = await api.get('/leads/attention');
    return data;
  }
  return { items: [], role: 'team_leader' };
}

export async function getBriefing() {
  if (shouldUseApi()) {
    const { data } = await api.get('/leads/briefing');
    return data;
  }
  return {
    role: 'team_leader',
    cards: {
      hot_leads_overnight: seedLeads.filter((l) => l.temperature === 'hot').length,
      showings_today: seedShowings.length,
      pending_verification: seedLeads.filter((l) => !seedVerificationByLeadId[l.id]).length,
      avg_response_minutes: 1.38,
    },
    attention_items: [],
  };
}

export async function createLead(payload) {
  if (shouldUseApi()) {
    const { data } = await api.post('/leads', payload);
    return data;
  }
  throw new Error('createLead requires live API');
}

export async function updateLead(id, payload) {
  if (shouldUseApi()) {
    const { data } = await api.patch(`/leads/${id}`, payload);
    return data;
  }
  throw new Error('updateLead requires live API');
}

export async function assignLead(id, agentId) {
  if (shouldUseApi()) {
    const { data } = await api.post(`/leads/${id}/assign`, { agent_id: agentId });
    return data;
  }
  throw new Error('assignLead requires live API');
}

export async function getVerification(leadId) {
  if (shouldUseApi()) {
    const { data } = await api.get(`/verification/${leadId}`);
    return data;
  }
  return { lead_id: leadId, scorecard: seedVerificationByLeadId[leadId] || null };
}

export async function listConversations(params = {}) {
  if (shouldUseApi()) {
    const { data } = await api.get('/conversations', { params });
    return {
      ...data,
      conversations: (data.conversations || []).map(normalizeConversation),
    };
  }
  return { conversations: seedConversations };
}

export async function listShowings(params = {}) {
  if (shouldUseApi()) {
    const { data } = await api.get('/showings', { params });
    return {
      ...data,
      showings: (data.showings || []).map(normalizeShowing),
    };
  }
  return { showings: seedShowings };
}

export async function updateShowing(id, payload) {
  if (shouldUseApi()) {
    const { data } = await api.patch(`/showings/${id}`, payload);
    return normalizeShowing(data);
  }
  // Demo fallback: merge locally so the row updates without a backend.
  const existing = seedShowings.find((s) => s.id === id);
  return { ...(existing || { id }), ...payload };
}

export async function nudgeAgent({ leadId, agentId }) {
  if (shouldUseApi()) {
    const { data } = await api.post('/notifications/agent-nudge', {
      lead_id: leadId,
      agent_id: agentId || undefined,
    });
    return data;
  }
  return { lead_id: leadId, agent_id: agentId, nudged: true };
}

export async function listProperties(params = {}) {
  if (shouldUseApi()) {
    const { data } = await api.get('/properties', { params });
    return data;
  }
  return { properties: seedProperties };
}

export async function listProjects(params = {}) {
  if (shouldUseApi()) {
    const { data } = await api.get('/projects', { params });
    return data;
  }
  return { projects: seedProjects };
}

export async function listTeam() {
  if (shouldUseApi()) {
    const { data } = await api.get('/team');
    return {
      ...data,
      members: (data.members || data.agents || []).map(normalizeAgent),
    };
  }
  return { members: seedAgents };
}

export async function analyticsOverview() {
  if (shouldUseApi()) {
    const { data } = await api.get('/analytics/overview');
    return data;
  }
  return { cards: {} };
}

export async function analyticsPipeline() {
  if (shouldUseApi()) {
    const { data } = await api.get('/analytics/pipeline');
    return data;
  }
  return { stages: [] };
}

export async function analyticsSources() {
  if (shouldUseApi()) {
    const { data } = await api.get('/analytics/sources');
    return data;
  }
  return { sources: [] };
}

export async function analyticsAgents() {
  if (shouldUseApi()) {
    const { data } = await api.get('/analytics/agents');
    return data;
  }
  return { agents: [] };
}

export async function analyticsResponseTime() {
  if (shouldUseApi()) {
    const { data } = await api.get('/analytics/response-time');
    return data;
  }
  return { avg_minutes: 1.38, sample_size: 0, series: [] };
}

// ── Admin command center ─────────────────────────────────────────────────────
//
// Every /admin/* route is super_admin-gated on the backend. These helpers
// skip the demo-seed fallback entirely — the admin dashboard is only
// reachable when a real JWT is present.

export async function adminListOrgs() {
  const { data } = await api.get('/admin/orgs');
  return data;
}

export async function adminCreateOrg(payload) {
  const { data } = await api.post('/admin/orgs', payload);
  return data;
}

export async function adminGetOrg(orgId) {
  const { data } = await api.get(`/admin/orgs/${orgId}`);
  return data;
}

export async function adminUpdateOrg(orgId, payload) {
  const { data } = await api.patch(`/admin/orgs/${orgId}`, payload);
  return data;
}

export async function adminGrantCredits(orgId, payload) {
  const { data } = await api.post(`/admin/orgs/${orgId}/grant-credits`, payload);
  return data;
}

export async function adminCreateUser(orgId, payload) {
  const { data } = await api.post(`/admin/orgs/${orgId}/users`, payload);
  return data;
}

export async function adminDeleteUser(userId) {
  const { data } = await api.delete(`/admin/users/${userId}`);
  return data;
}

export async function adminUsage() {
  const { data } = await api.get('/admin/usage');
  return data;
}

// ── Per-org integration credentials ─────────────────────────────────────────

export async function adminListIntegrations(orgId) {
  const { data } = await api.get(`/admin/orgs/${orgId}/integrations`);
  return data;
}

export async function adminSaveGhlIntegration(orgId, payload) {
  const { data } = await api.post(
    `/admin/orgs/${orgId}/integrations/ghl`,
    payload,
  );
  return data;
}

export async function adminSaveTwilioIntegration(orgId, payload) {
  const { data } = await api.post(
    `/admin/orgs/${orgId}/integrations/twilio`,
    payload,
  );
  return data;
}

export async function adminSaveVapiIntegration(orgId, payload) {
  const { data } = await api.post(
    `/admin/orgs/${orgId}/integrations/vapi`,
    payload,
  );
  return data;
}

export async function adminDisconnectIntegration(orgId, integrationType) {
  const { data } = await api.delete(
    `/admin/orgs/${orgId}/integrations/${integrationType}`,
  );
  return data;
}

export async function adminTestIntegration(orgId, integrationType) {
  const { data } = await api.post(
    `/admin/orgs/${orgId}/integrations/${integrationType}/test`,
  );
  return data;
}

// ── Per-org AI config ──────────────────────────────────────────────────────

export async function adminGetAIConfig(orgId) {
  const { data } = await api.get(`/admin/orgs/${orgId}/ai-config`);
  return data;
}

export async function adminSaveAIConfig(orgId, payload) {
  const { data } = await api.put(`/admin/orgs/${orgId}/ai-config`, payload);
  return data;
}

// ── Per-org listings ───────────────────────────────────────────────────────

export async function adminListListings(orgId) {
  const { data } = await api.get(`/admin/orgs/${orgId}/listings`);
  return data;
}

export async function adminCreateListing(orgId, payload) {
  const { data } = await api.post(`/admin/orgs/${orgId}/listings`, payload);
  return data;
}

export async function adminUpdateListing(orgId, listingId, payload) {
  const { data } = await api.patch(
    `/admin/orgs/${orgId}/listings/${listingId}`,
    payload,
  );
  return data;
}

export async function adminDeleteListing(orgId, listingId) {
  const { data } = await api.delete(
    `/admin/orgs/${orgId}/listings/${listingId}`,
  );
  return data;
}

// ── Per-org pipelines ──────────────────────────────────────────────────────

export async function adminListPipelines(orgId) {
  const { data } = await api.get(`/admin/orgs/${orgId}/pipelines`);
  return data;
}

export async function adminCreatePipeline(orgId, payload) {
  const { data } = await api.post(`/admin/orgs/${orgId}/pipelines`, payload);
  return data;
}

export async function adminUpdatePipeline(orgId, pipelineId, payload) {
  const { data } = await api.patch(
    `/admin/orgs/${orgId}/pipelines/${pipelineId}`,
    payload,
  );
  return data;
}

export async function adminDeletePipeline(orgId, pipelineId) {
  const { data } = await api.delete(
    `/admin/orgs/${orgId}/pipelines/${pipelineId}`,
  );
  return data;
}

export async function getCredits() {
  if (shouldUseApi()) {
    const { data } = await api.get('/billing/credits');
    return data;
  }
  const included = 500;
  const purchased = 100;
  const used = 247;
  return {
    included,
    purchased,
    used,
    remaining: Math.max(0, included + purchased - used),
    total: included + purchased,
  };
}

// ── Stripe billing (admin) ──────────────────────────────────────────────────

export async function getBillingStatus(orgId) {
  const { data } = await api.get(`/billing/status/${orgId}`);
  return data;
}

export async function createBillingCheckout({ orgId, planTier }) {
  const { data } = await api.post('/billing/create-checkout', {
    org_id: orgId,
    plan_tier: planTier,
  });
  return data;
}

export default api;
